import {
  v1AuthorsDetail,
  v1AuthorsHeadlinesPaginatedDetail,
  type V1AuthorsHeadlinesPaginatedDetailParams,
} from '@on3/api';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import withAuthentication from '@on3/ui-lib/hocs/withAuthentication';
import withValidUrl from '@on3/ui-lib/hocs/withValidUrl';
import { getFulfilledValue, proxyApi } from '@on3/ui-lib/utils/api';
import { userHasPermission } from '@on3/ui-lib/utils/auth/access';
import { customerServiceLevel } from '@on3/ui-lib/utils/auth/constants';
import { PageWrapper } from '@public/components/Layouts/PageWrapper';
import { RightSidebarLayout } from '@public/components/Layouts/RightSidebarLayout';
import { StandardSidebar } from '@public/components/Layouts/StandardSidebar';
import {
  AuthorFeed,
  type IAuthorFeedProps,
} from '@public/components/Writers/Feed/AuthorFeed';
import { createCanonicalUrl } from '@public/utils/meta';
import type { AxiosError } from 'axios';
import type { GetServerSideProps } from 'next';
import { usePathname } from 'next/navigation';

export interface AuthorPageProps extends IGenericPageProps, IAuthorFeedProps {}

const UserProfilePage = (props: AuthorPageProps) => {
  const authorName = props.userProfileResponse?.author?.name;
  const articles = props.userArticles?.list || [];
  const title = `${authorName} ${
    articles.length > 0 ? 'Articles ' : ''
  }| On3.com`;
  const description = `Discover the latest ${authorName}' in-depth articles on college sports, focusing on the NIL, transfer portal news, and NCAA developments, at On3.com.`;
  const pathname = usePathname();
  const canonical = createCanonicalUrl(pathname);

  return (
    <PageWrapper
      {...props}
      canonical={canonical}
      description={description}
      title={title}
    >
      <RightSidebarLayout>
        <AuthorFeed {...props} />
        <StandardSidebar />
      </RightSidebarLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const siteData = await withValidUrl.isValid(ctx);
  const userData = await withAuthentication.isAuth(ctx);

  if (!siteData) {
    return {
      notFound: true,
    };
  }

  try {
    const { slug, page = 1 } = ctx.query as { slug: string; page: string };
    const params: V1AuthorsHeadlinesPaginatedDetailParams = {
      page: +page,
      author: slug,
    };
    const [userArticles, userProfileResponse] = await Promise.allSettled([
      v1AuthorsHeadlinesPaginatedDetail(proxyApi, params),
      v1AuthorsDetail(proxyApi, slug),
    ]);

    const articlesList = getFulfilledValue(userArticles);
    const authorResponse = getFulfilledValue(userProfileResponse);

    if (
      !articlesList?.list?.length &&
      !authorResponse?.author?.jobTitle &&
      !userHasPermission(customerServiceLevel, userData)
    ) {
      return {
        notFound: true,
      };
    }

    return {
      props: {
        pageType: 'category',
        siteData,
        userData: userData || {},
        userArticles: articlesList,
        userProfileResponse: authorResponse,
      },
    };
  } catch (error) {
    const err: AxiosError<unknown> = error as AxiosError<unknown>;

    console.log(err.message);

    return { props: {} };
  }
};

export default withValidUrl(UserProfilePage);
